import { Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { useRepairCostEstimatorSearch } from '../hooks/useRepairCostEstimatorSearch';
import { AnalysisCompletedMessage } from '../../components/AnalysisCompletedMessage';
import { RepairDataForm } from './RepairDataForm';
import { MarketValueForm } from '../../components/MarketValueForm';
import { AutoAwesomeOutlined } from '@mui/icons-material';

interface IEnterRepairDataInstructionProps {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  hasConversationStarted: boolean;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  isLoading: boolean;
}

/**
 * EnterRepairDataInstruction - Shows instruction to enter repair data
 * The input fields are only visible when conversation hasn't started yet
 * Sends repair data to the RepairCostEstimator bot
 * @component
 */
export const EnterRepairDataInstruction = ({
  hasConversationStarted,
  onSendMessage,
  shouldResetInputs = false,
  onResetComplete,
  onProcessingStateChange,
  isLoading,
}: IEnterRepairDataInstructionProps) => {
  const {
    vinOrVehicleInfo,
    location,
    repairDescription,
    shopQuote,
    marketValue,
    error,
    hasSubmitted,
    hasSubmittedMarketValue,
    searchType,
    showMarketValueInput,
    handleVinOrVehicleInfoChange,
    handleLocationChange,
    handleRepairDescriptionChange,
    handleShopQuoteChange,
    handleMarketValueChange,
    handleSubmit,
    handleMarketValueSubmit,
  } = useRepairCostEstimatorSearch({
    onSendMessage,
    shouldResetInputs,
    onResetComplete,
    onProcessingStateChange,
  });
  const theme = useTheme();

  const searchLabel = searchType || 'Vehicle';

  // Show form when:
  // 1. Initial state (nothing submitted)
  // 2. OR when market value input is needed (after vehicle search but before final submit)
  const hasSubmittedAny = hasSubmitted || hasSubmittedMarketValue;
  const showForm =
    !isLoading &&
    (showMarketValueInput || (!hasSubmittedAny && !hasConversationStarted));
  const showAnalyzingStatus =
    (hasSubmitted || hasConversationStarted) &&
    !isLoading &&
    !showMarketValueInput;

  return (
    <Stack width="100%" justifyContent="center" alignItems="center">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        p={2}
        mb={2}
        sx={{ backgroundColor: 'background.default', borderRadius: 2 }}
      >
        <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
        <Text variant="body1">
          Hi! I&apos;m your AI vehicle repair assistant. I&apos;d be happy to
          help you estimate the repair costs for your vehicle.
        </Text>
      </Stack>
      <Stack width="100%" maxWidth={800}>
        {showForm && (
          <Stack
            direction="column"
            spacing={1}
            width={{ xs: '100%', md: 'fit-content' }}
            maxWidth="760px"
            p={{ xs: 2, md: 2.5 }}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            {showMarketValueInput ? (
              <MarketValueForm
                marketValue={marketValue}
                error={error}
                onMarketValueChange={handleMarketValueChange}
                onSubmit={handleMarketValueSubmit}
              />
            ) : (
              <RepairDataForm
                vinOrVehicleInfo={vinOrVehicleInfo}
                location={location}
                repairDescription={repairDescription}
                shopQuote={shopQuote}
                error={error}
                isLoading={isLoading}
                onVinOrVehicleInfoChange={handleVinOrVehicleInfoChange}
                onLocationChange={handleLocationChange}
                onRepairDescriptionChange={handleRepairDescriptionChange}
                onShopQuoteChange={handleShopQuoteChange}
                onSubmit={handleSubmit}
              />
            )}
          </Stack>
        )}
        {showAnalyzingStatus && (
          <AnalysisCompletedMessage
            analysisType="repair costs"
            primaryValue={repairDescription}
            secondaryValue={vinOrVehicleInfo}
            location={location}
          />
        )}
      </Stack>
    </Stack>
  );
};
